import { apiClient } from './api-client';
import type {
  CommercialOutcome,
  ModificationAuthorKind,
  ModificationClassification,
  ModificationEntryType,
  ModificationEntryVisibility,
  ModificationOwner,
  ModificationRoundState,
  ModificationWaitingFor,
  OpsFeasibilityVerdict,
  OpsScopeImpact,
  QuotationRevisionStatus,
} from './inspection-modification.service';

/**
 * Request Thread Stage C — the staff workspace's ONE data source.
 *
 * Mirrors `StaffRequestThreadDto` exactly. The composed read exists so the
 * workspace asks once and renders a single consistent snapshot instead of
 * stitching the round, its timeline, the job and the quotation together from
 * four endpoints that can arrive out of step — so this module deliberately
 * exposes ONE call and no others. A second fetch from the thread page would
 * defeat the endpoint's whole reason for existing.
 *
 * The workflow vocabulary is imported from `inspection-modification.service`
 * rather than restated: there is one set of state names in this application.
 */

/** Who acted. `name` is null where the record does not say — a backfilled
 * genesis entry carries neither user nor contact, and no name is invented. */
export interface ThreadActor {
  name: string | null;
}

/**
 * The soft claim, as attribution and never as authority.
 *
 * `assignedUserId` is claim-light: set by the first staff act in a department
 * and never cleared. It gates nothing server-side and must gate nothing here.
 * Rendering it is allowed — "Assigned to {name}" is a true statement about the
 * record — but it must never produce a first-person claim, and it must never
 * decide what the reader may do. Ownership is `currentOwner` / `waitingFor`;
 * eligibility is `capabilities`.
 */
export interface ThreadAssignee {
  userId: string;
  name: string;
}

/** The JOB's priority. `null` is a real answer and is never defaulted. */
export interface ThreadPriority {
  id: string;
  name: string;
  colorHex: string;
  isEmergency: boolean;
}

export interface ThreadInspection {
  id: string;
  inspectionNumber: string;
  status: string;
  customerName: string | null;
  equipmentName: string | null;
  priority: ThreadPriority | null;
}

/** A quotation revision by its identity only — no amount, no asset, no key. */
export interface ThreadRevision {
  id: string;
  revisionNumber: number;
  status: QuotationRevisionStatus;
  quotationReference: string | null;
  sentAt: string | null;
}

export interface ThreadRevisionContext {
  against: ThreadRevision;
  resulting: ThreadRevision | null;
}

export interface ThreadChainNeighbour {
  roundNumber: number;
  roundId: string;
}

export interface ThreadChain {
  requestNumber: number;
  totalRequests: number;
  previous: ThreadChainNeighbour | null;
  next: ThreadChainNeighbour | null;
}

/**
 * One timeline entry.
 *
 * Entries the caller may not see are excluded by the SERVER, in the query — the
 * client neither receives nor filters them. There is deliberately no
 * `actorRoleName`: no historical role is stored, and a live lookup would
 * re-label every past entry when someone changed department.
 */
export interface ThreadTimelineEntry {
  id: string;
  entryType: ModificationEntryType;
  authorKind: ModificationAuthorKind;
  actor: ThreadActor;
  /** The department this entry handed the round TO, from the stored hop. Null
   * when the entry is not a hop — legitimately sparse. */
  department: ModificationOwner | null;
  visibility: ModificationEntryVisibility;
  body: string | null;
  fromOwner: ModificationOwner | null;
  toOwner: ModificationOwner | null;
  fromWaitingFor: ModificationWaitingFor | null;
  toWaitingFor: ModificationWaitingFor | null;
  /**
   * Stage E-iv — the quotation revision THIS entry decided, supplied by the
   * server from the persisted `decisionEntryId` relation. Null on every other
   * entry. The timeline renders it and decides nothing: it never compares
   * revision numbers, references, timestamps or neighbouring entries.
   *
   * STAFF ONLY. The customer thread has its own read model and never carries it.
   */
  revision?: ThreadRevision | null;
  createdAt: string;
}

/**
 * The customer's ask, in both forms.
 *
 * Two different facts. `original` is the archival opening statement; `latest` is
 * the newest customer-meaning entry, which may say something else entirely. Both
 * arrive VERBATIM — no trimming, normalising or rewriting anywhere, on either
 * side of the wire.
 */
export interface ThreadRequest {
  original: string;
  latest: string | null;
}

/**
 * What THIS caller may do, decided by the SERVER from state ∧ permission.
 *
 * The client never computes these. Not from a role name, not from a department,
 * not from `assignedUserId`, and not from the state alone — the last is the
 * subtle one: state without permission would offer an Operations user the
 * Commercial close button on a COMMERCIAL_DECISION round.
 *
 * A capability is a UX gate, never the boundary: the backend answers 403 and the
 * state machine answers 409 whatever is rendered.
 */
export interface ThreadCapabilities {
  canClassify: boolean;
  canRecordOpsVerdict: boolean;
  canReferToOps: boolean;
  canRequestClarification: boolean;
  canPublishOpsNote: boolean;
  canClose: boolean;
  allowedClosureOutcomes: CommercialOutcome[];
  canSendRevision: boolean;
  canAddInternalNote: boolean;
  /** Does this caller have something to do here now? Server-derived from the
   * capabilities above — never a department comparison, never an assignee. */
  actionRequired: boolean;
}

export interface StaffRequestThread {
  roundId: string;
  roundNumber: number;
  state: ModificationRoundState;
  /** Authoritative workflow ownership. Null once the round is closed. */
  currentOwner: ModificationOwner | null;
  /** Who owes the next act; the only axis that may be CUSTOMER. */
  waitingFor: ModificationWaitingFor | null;
  classification: ModificationClassification | null;
  opsVerdict: OpsFeasibilityVerdict | null;
  opsTechnicalNote: string | null;
  opsAlternativeDescription: string | null;
  opsScopeImpact: OpsScopeImpact | null;
  opsAffectsParts: boolean;
  opsAffectsLabour: boolean;
  commercialOutcome: CommercialOutcome | null;
  closureMessage: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;

  request: ThreadRequest;
  assignee: ThreadAssignee | null;
  inspection: ThreadInspection;
  revisions: ThreadRevisionContext;
  chain: ThreadChain;
  timeline: ThreadTimelineEntry[];
  capabilities: ThreadCapabilities;
}

/** The React Query key for the thread. One key, one request, one snapshot. */
export const REQUEST_THREAD_KEY = 'request-thread';

export const requestThreadService = {
  /**
   * The whole workspace in one call.
   *
   * A round in another tenant and a round that does not exist both answer 404 —
   * the server folds the tenant predicate into the same lookup as the id, and no
   * client-side handling may try to tell them apart.
   */
  getThread: (roundId: string): Promise<StaffRequestThread> =>
    apiClient.get<StaffRequestThread>(`/modification-rounds/${roundId}/thread`).then((r) => r.data),
};
